/**
 * NewTopicDialog — the composer for the community forum. One dialog handles
 * every kind: plain topic, poll (with dynamic options + multi-select flag),
 * scheduled event (starts_at + optional location/url). Announcements are
 * admin-only and expose a checkbox that flips `kind` to 'announcement'.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { MessageSquare, BarChart3, Calendar, Megaphone, Plus, X } from 'lucide-react';
import ForumImageUploader from './ForumImageUploader';

const KINDS = [
  { id: 'topic',        label: 'Topic',        icon: MessageSquare },
  { id: 'poll',         label: 'Poll',         icon: BarChart3 },
  { id: 'event',        label: 'Event',        icon: Calendar },
];

const NewTopicDialog = ({ open, onClose, categories, onCreated, defaultCategoryId, defaultTitle = '' }) => {
  const { API, user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  const [kind, setKind] = useState('topic');
  const [categoryId, setCategoryId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [tagsRaw, setTagsRaw] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollMulti, setPollMulti] = useState(false);
  const [eventDate, setEventDate] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventUrl, setEventUrl] = useState('');
  const [images, setImages] = useState([]);
  const [asAnnouncement, setAsAnnouncement] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset form each time the dialog opens; prefer the passed default cat.
    const nonBridge = categories.filter((c) => !c.is_lesson_bridge);
    setCategoryId(defaultCategoryId || nonBridge[0]?.id || '');
    setKind('topic');
    setTitle(defaultTitle || '');
    setBody('');
    setTagsRaw('');
    setPollOptions(['', '']);
    setPollMulti(false);
    setEventDate('');
    setEventLocation('');
    setEventUrl('');
    setAsAnnouncement(false);
  }, [open, defaultCategoryId, defaultTitle, categories]);

  const submit = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (!categoryId) { toast.error('Pick a category'); return; }
    if (kind === 'poll') {
      const valid = pollOptions.map((o) => o.trim()).filter(Boolean);
      if (valid.length < 2) { toast.error('Polls need at least 2 options'); return; }
    }
    if (kind === 'event' && !eventDate) {
      toast.error('Events need a start date/time'); return;
    }
    setSubmitting(true);
    try {
      // Append uploaded images as markdown so they render inline.
      const imgMarkdown = (images || []).map((im) => `![${im.filename}](${im.url})`).join('\n');
      const bodyWithImages = [body.trim(), imgMarkdown].filter(Boolean).join('\n\n');
      const payload = {
        category_id: categoryId,
        title: title.trim(),
        body_md: bodyWithImages,
        tags: tagsRaw.split(',').map((s) => s.trim()).filter(Boolean),
        kind: (isAdmin && asAnnouncement) ? 'announcement' : kind,
      };
      if (kind === 'poll') {
        payload.poll_options = pollOptions.map((o) => o.trim()).filter(Boolean);
        payload.poll_multi = pollMulti;
      }
      if (kind === 'event') {
        payload.event_starts_at = new Date(eventDate).toISOString();
        payload.event_location = eventLocation.trim() || null;
        payload.event_url = eventUrl.trim() || null;
      }
      const r = await axios.post(`${API}/forum/topics`, payload);
      toast.success('Topic posted');
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post');
    } finally { setSubmitting(false); }
  };

  const setPollOptionAt = (i, v) => {
    setPollOptions((prev) => prev.map((o, idx) => idx === i ? v : o));
  };

  const selectableCategories = categories.filter((c) => !c.is_lesson_bridge);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="new-topic-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Start a new topic</DialogTitle>
          <DialogDescription>Ask a question, share a recipe, or announce an event.</DialogDescription>
        </DialogHeader>

        {/* Kind switcher */}
        <div className="flex items-center gap-1.5 flex-wrap" role="tablist" aria-label="Post kind">
          {KINDS.map((k) => {
            const active = kind === k.id;
            return (
              <button
                key={k.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setKind(k.id)}
                data-testid={`new-topic-kind-${k.id}`}
                className={`inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-xs font-bold transition-colors
                  ${active ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground/70 hover:text-foreground'}`}
              >
                <k.icon className="w-3.5 h-3.5" />
                {k.label}
              </button>
            );
          })}
          {isAdmin && (
            <label className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 cursor-pointer">
              <input
                type="checkbox"
                checked={asAnnouncement}
                onChange={(e) => setAsAnnouncement(e.target.checked)}
                data-testid="new-topic-announcement-checkbox"
                className="rounded"
              />
              <Megaphone className="w-3.5 h-3.5" /> Post as announcement
            </label>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Category</Label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              data-testid="new-topic-category-select"
              className="w-full mt-1 p-2 border border-border rounded-lg bg-background text-sm"
            >
              {selectableCategories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-xs">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short, descriptive title"
              maxLength={220}
              data-testid="new-topic-title-input"
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-xs">Body {kind === 'poll' && <span className="text-muted-foreground">(context for the poll — optional)</span>}</Label>
            <Textarea
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Markdown supported. What are you asking or sharing?"
              data-testid="new-topic-body-input"
              className="mt-1"
            />
          </div>

          {/* Inline image uploads — appended as markdown when posted so they
              render inline in the topic view. */}
          <ForumImageUploader
            value={images}
            onChange={setImages}
            testIdPrefix="new-topic-image"
          />

          {kind === 'poll' && (
            <div className="space-y-2" data-testid="new-topic-poll-section">
              <Label className="text-xs">Poll options</Label>
              {pollOptions.map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={o}
                    onChange={(e) => setPollOptionAt(i, e.target.value)}
                    placeholder={`Option ${i + 1}`}
                    maxLength={80}
                    data-testid={`new-topic-poll-option-${i}`}
                  />
                  {pollOptions.length > 2 && (
                    <button
                      type="button"
                      onClick={() => setPollOptions((prev) => prev.filter((_, idx) => idx !== i))}
                      aria-label="Remove option"
                      className="h-8 w-8 rounded-full text-muted-foreground hover:text-destructive hover:bg-muted flex items-center justify-center"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {pollOptions.length < 8 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setPollOptions((prev) => [...prev, ''])}
                  className="rounded-full"
                  data-testid="new-topic-poll-add-option"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add option
                </Button>
              )}
              <label className="inline-flex items-center gap-2 text-xs text-foreground/80 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pollMulti}
                  onChange={(e) => setPollMulti(e.target.checked)}
                  data-testid="new-topic-poll-multi"
                />
                Allow multiple selections
              </label>
            </div>
          )}

          {kind === 'event' && (
            <div className="space-y-2" data-testid="new-topic-event-section">
              <div>
                <Label className="text-xs">When</Label>
                <Input
                  type="datetime-local"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  data-testid="new-topic-event-date"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Location / meeting details (optional)</Label>
                <Input
                  value={eventLocation}
                  onChange={(e) => setEventLocation(e.target.value)}
                  placeholder="Zoom, in-person address, etc."
                  data-testid="new-topic-event-location"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Event link (optional)</Label>
                <Input
                  value={eventUrl}
                  onChange={(e) => setEventUrl(e.target.value)}
                  placeholder="https://…"
                  data-testid="new-topic-event-url"
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">Tags (comma-separated, optional)</Label>
            <Input
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value)}
              placeholder="e.g. beginner, moisture, candy"
              data-testid="new-topic-tags-input"
              className="mt-1"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="rounded-full font-bold" data-testid="new-topic-submit">
            {submitting ? 'Posting…' : 'Post'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewTopicDialog;
